import EmojiPlugin from '@components/mentionsTextarea/EmojiPlugin'
import { initiateRecognitionAttachmentUpload } from '@kudos/http-client'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import AttachmentsField from '@shared/forms/AttachmentsField'
import GiphyPluginField from '@shared/forms/mentionsTextareaField/GiphyPluginField'
import MentionsTextareaField from '@shared/forms/mentionsTextareaField/MentionsTextareaField'
import { MAX_MESSAGE_CHARS } from '@shared/wallPost/constants'
import { T, useT } from '@transifex/react'
import { Control, FieldValues, Path } from 'react-hook-form'
import RefineMessageButton from './RefineMessageButton'

type MessageAndAttachmentsSectionProps<T extends FieldValues> = {
  control: Control<T>
  canRefineMessage: boolean
  canUpgradeLicense: boolean
  includeAttachments?: boolean
  includeGif?: boolean
  autoFocus?: boolean
}

const MessageAndAttachmentsSection = <T extends FieldValues>({
  control,
  canRefineMessage,
  canUpgradeLicense,
  includeAttachments = true,
  includeGif = true,
  autoFocus = false,
}: MessageAndAttachmentsSectionProps<T>) => {
  const t = useT()

  return (
    <>
      <MentionsTextareaField
        name={'message' as Path<T>}
        control={control}
        placeholder={
          <T _str="Make it meaningful! Describe their contributions and the positive impact they've had on you or the organization. Type @ to tag someone." />
        }
        maxChars={MAX_MESSAGE_CHARS}
        ariaLabel={t('Write a message')}
        label={<T _str="Message" />}
        isMentionsSupported
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}>
        <Stack direction="row" width="100%" justifyContent="space-between">
          {canRefineMessage || canUpgradeLicense ? <RefineMessageButton /> : <Box />}
          <Stack direction="row" alignContent="center" useFlexGap gap={0.5}></Stack>
        </Stack>
        <EmojiPlugin id="emojiSendRecognition" />
        {includeGif && (
          <GiphyPluginField name={'gif' as Path<T>} control={control} id="sendRecognition" />
        )}
      </MentionsTextareaField>
      {includeAttachments && (
        <AttachmentsField
          name="attachments"
          control={control as unknown as Control<FieldValues>}
          mutationFn={initiateRecognitionAttachmentUpload}
        />
      )}
    </>
  )
}

export default MessageAndAttachmentsSection
